'use client'

import { useSearchParams } from 'next/navigation'

import {
  type ComponentProps,
  type MouseEvent,
  useEffect,
  useRef,
  useTransition,
} from 'react'
import { useProgress } from 'react-transition-progress'

import isString from 'lodash/fp/isString'

import {
  Link as NextI18nLink,
  usePathname,
  useRouter,
} from '../helpers/translations'
import { formatUrl } from '../helpers/url'

const SEARCH_PARAMS_TO_KEEP = new Set([
  'asRole',
  'auth_token',
  'authToken',
  'hasMax',
  'hasPlus',
  'maxRole',
  'mediaRole',
  'toolbar',
])

// Copied from
// https://github.com/vercel/react-transition-progress/blob/main/src/next.tsx#L10C1-L22C2
// which was copied from
// https://github.com/vercel/next.js/blob/canary/packages/next/src/client/link.tsx#L180-L191.
function isModifiedEvent(event: React.MouseEvent): boolean {
  const eventTarget = event.currentTarget as HTMLAnchorElement | SVGAElement
  const target = eventTarget.getAttribute('target')

  return (
    (target && target !== '_self') ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    // Triggers resource download.
    (event.nativeEvent && event.nativeEvent.which === 2)
  )
}

/**
 * A custom Link component to handle route transition/progress and i18n.
 * Based on `react-transition-progress`.
 * See https://github.com/vercel/react-transition-progress/blob/main/src/next.tsx
 */
export default function Link({
  children,
  href,
  onClick,
  replace,
  scroll,
  ...rest
}: ComponentProps<typeof NextI18nLink>) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const startProgress = useProgress()
  const [isPending, startTransition] = useTransition()

  const startProgressTimer = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if (startProgressTimer.current !== null && !isPending) {
      clearTimeout(startProgressTimer.current)
    }
  }, [isPending])

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (isModifiedEvent(event)) {
      return
    }

    event.preventDefault()

    startTransition(() => {
      startProgressTimer.current = setTimeout(startProgress, 100)

      let url = isString(href) ? href : formatUrl(href)

      // Keep track of the article we are coming from when leaving an article.
      // This way we can log some analytics metrics if needed.
      if (pathname.startsWith('/article/')) {
        url += `${url.includes('?') ? '&' : '?'}from=${pathname.split('/').pop()?.replace('?', '&')}`
      }

      if (searchParams.has('auth_token') || searchParams.has('authToken')) {
        url += `${url.includes('?') ? '&' : '?'}auth_token=${searchParams.get('auth_token')}`
      }

      searchParams.forEach((value, key) => {
        if (
          SEARCH_PARAMS_TO_KEEP.has(key) ||
          key.toLowerCase().includes('debug')
        ) {
          url += `${url.includes('?') ? '&' : '?'}${key}=${value}`
        }
      })

      if (replace) {
        router.replace(url, { scroll })
      } else {
        router.push(url, { scroll })
      }
    })

    onClick?.(event)
  }

  return (
    <NextI18nLink href={href} onClick={handleClick} {...rest}>
      {children}
    </NextI18nLink>
  )
}
